import * as React from 'react';
import { useDiagnosisPermission } from '../../../contexts/DiagnosisPermissionContext';
import { forwardRef, useRef, useState } from 'react';
import {
  Stack,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
} from '@mui/material';
import toast from 'react-hot-toast';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { GridRenderEditCellParams } from '@mui/x-data-grid';

import { CrudDataGrid, type CrudConfig, type CrudDataGridHandle } from '../../CrudDataGrid';
import {
  DiagnosisEnergyVectorsCreateDTO,
  DiagnosisEnergyVectorsReadDTO,
  EnergyVectorsReadDTO,
  CopyEnergyVectorDto,
  ICopyEnergyVectorDto,
} from '../../../generated/api-client';
import { apiClient } from '../../../services/apiClient';
import { DiagnosisStepHandle } from './Step.types';
import { useVectorTypeOptions } from '../../../hooks/useVectorTypeOptions';
import { DECIMAL_PLACES } from '../../../constants/decimalPlacesPolicy';
import { ActionSpacer, UnitOfMeasurementEditCell } from '../../common';
import {
  AutocompleteEditCell,
  getOptionLabelFromValue,
} from '../../common/AutocompleteEditCell';
import { formatUnitOfMeasurement } from '../../../utils/unitOfMeasurementFormatter';

interface DiagnosisEnergyVectorsStepProps {
  diagnosisId?: string;
}

export const DiagnosisEnergyVectorsStep = React.memo(
  forwardRef<DiagnosisStepHandle, DiagnosisEnergyVectorsStepProps>(({ diagnosisId }, ref) => {
    const gridRef = useRef<CrudDataGridHandle>(null);
    const queryClient = useQueryClient();
    const { data: vectorTypeOptions = [] } = useVectorTypeOptions();
    const [dialogOpen, setDialogOpen] = useState(false);
    const [selectedVectorId, setSelectedVectorId] = useState<string>('');

    // Carica i vettori energetici generali
    const { data: generalVectors = [], isLoading: isLoadingVectors } = useQuery<
      EnergyVectorsReadDTO[]
    >('general-energy-vectors', () => apiClient.list_items_energy_vectors__get(), {
      enabled: dialogOpen,
    });

    // Mutation per copiare il vettore
    const copyMutation = useMutation(
      async (dto: CopyEnergyVectorDto) => {
        return await apiClient.copy_from_general_diagnosis_energy_vectors_copy_from_general_post(
          dto
        );
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries(`diagnosis-energy-vectors-${diagnosisId}`);
          toast.success('Vettore energetico copiato con successo');
          const selectedVector = generalVectors.find(v => v.id === selectedVectorId);
          const isNonElectricOrGas =
            selectedVector &&
            selectedVector.vector_type !== 'Electricity' &&
            selectedVector.vector_type !== 'NaturalGas';
          if (isNonElectricOrGas) {
            toast.success('Contatore creato con successo');
          }
          setDialogOpen(false);
          setSelectedVectorId('');
        },
        onError: (error: any) => {
          toast.error(error?.message || 'Errore nella copia del vettore energetico');
        },
      }
    );

    const handleCopyVector = () => {
      if (!selectedVectorId || !diagnosisId) {
        toast.error('Seleziona un vettore energetico');
        return;
      }
      copyMutation.mutate({
        energy_vector_id: selectedVectorId,
        diagnosis_id: diagnosisId,
      } as any);
    };

    const handleAddCustom = () => {
      setDialogOpen(false);
      gridRef.current?.addRow();
    };

    React.useImperativeHandle(ref, () => ({
      save: async () => {
        return { success: true };
      },
      add: async () => {
        if (!diagnosisId) {
          toast.error('Salva prima i dati principali della diagnosi.');
          return;
        }
        setDialogOpen(true);
      },
    }));

    if (!diagnosisId) {
      return (
        <Typography color="text.secondary">
          Salva le informazioni della diagnosi per gestire i vettori energetici.
        </Typography>
      );
    }

    const config: CrudConfig<DiagnosisEnergyVectorsReadDTO, DiagnosisEnergyVectorsCreateDTO> = {
      queryKey: `diagnosis-energy-vectors-${diagnosisId}`,
      fields: [
        {
          field: 'vector_type',
          headerName: 'Tipologia vettore',
          width: 200,
          editable: true,
          type: 'string',
          renderEditCell: (params: GridRenderEditCellParams) => (
            <AutocompleteEditCell
              {...params}
              options={vectorTypeOptions}
              placeholder="Tipologia vettore"
            />
          ),
          valueFormatter: (v) => getOptionLabelFromValue(vectorTypeOptions, v as string),
        },
        {
          field: 'unit_of_measurement',
          headerName: 'Unità di misura',
          width: 160,
          editable: true,
          type: 'string',
          renderEditCell: (params: GridRenderEditCellParams) => (
            <UnitOfMeasurementEditCell {...params} />
          ),
          valueFormatter: formatUnitOfMeasurement,
        },
        {
          field: 'co2_factor',
          headerName: 'Fattore kg CO₂',
          width: 140,
          editable: true,
          type: 'number',
          align: 'right',
          headerAlign: 'right',
          decimalPlaces: DECIMAL_PLACES.fattori_co2,
        },
        {
          field: 'tep_factor',
          headerName: 'Fattore tep',
          width: 140,
          editable: true,
          type: 'number',
          align: 'right',
          headerAlign: 'right',
          decimalPlaces: DECIMAL_PLACES.fattori_tep,
        },
        {
          field: 'tj_factor',
          headerName: 'Fattore TJ',
          width: 140,
          editable: true,
          type: 'number',
          align: 'right',
          headerAlign: 'right',
          decimalPlaces: DECIMAL_PLACES.fattori_tj,
        },
        {
          field: 'kwh_factor',
          headerName: 'Fattore kWh',
          width: 140,
          editable: true,
          type: 'number',
          align: 'right',
          headerAlign: 'right',
          decimalPlaces: DECIMAL_PLACES.fattori_kwh,
        },
        {
          field: 'density',
          headerName: 'Densità (kg/l)',
          width: 140,
          editable: true,
          type: 'number',
          align: 'right',
          headerAlign: 'right',
          decimalPlaces: 2,
        },
      ],
      listFn: () =>
        apiClient.search_by_diagnosis_id_diagnosis_energy_vectors_search_by_diagnosis_id__diagnosis_id__get(
          diagnosisId
        ),
      createFn: data =>
        apiClient.create_item_diagnosis_energy_vectors__post(
          new DiagnosisEnergyVectorsCreateDTO({
            ...data,
            diagnosis_id: diagnosisId,
          })
        ),
      updateFn: (id, data) =>
        apiClient.update_item_diagnosis_energy_vectors__item_id__put(
          id,
          new DiagnosisEnergyVectorsCreateDTO({
            ...data,
            diagnosis_id: diagnosisId,
          })
        ),
      deleteFn: id => apiClient.delete_item_diagnosis_energy_vectors__item_id__delete(id),
      getId: row => row.id ?? '',
      getCreateDto: row =>
        new DiagnosisEnergyVectorsCreateDTO({
          unit_of_measurement: row.unit_of_measurement ?? undefined,
          diagnosis_id: diagnosisId,
          table_code_index: row.table_code_index ?? '',
          code: row.code ?? '',
          vector_type: row.vector_type ?? undefined,
          co2_factor: row.co2_factor !== undefined ? Number(row.co2_factor) : (undefined as any),
          tep_factor: row.tep_factor !== undefined ? Number(row.tep_factor) : (undefined as any),
          tj_factor: row.tj_factor !== undefined ? Number(row.tj_factor) : (undefined as any),
          kwh_factor:
            row.kwh_factor !== undefined && row.kwh_factor !== null && row.kwh_factor !== ''
              ? Number(row.kwh_factor)
              : (undefined as any),
          density:
            row.density !== undefined && row.density !== null && row.density !== ''
              ? Number(row.density)
              : (undefined as any),
        }),
    };

    return (
      <>
        <Stack spacing={2} sx={{ p: 2 }}>
          <Typography variant="h6">Vettori energetici</Typography>
          <CrudDataGrid ref={gridRef} config={config} height="calc(100vh - 380px)" />
          <ActionSpacer></ActionSpacer>
        </Stack>

        {/* Dialog per selezionare vettore da copiare */}
        <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Aggiungi vettore energetico</DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 2 }}>
              <FormControl fullWidth>
                <InputLabel>Vettore energetico generale</InputLabel>
                <Select
                  value={selectedVectorId}
                  onChange={e => setSelectedVectorId(e.target.value)}
                  label="Vettore energetico generale"
                  disabled={isLoadingVectors}
                >
                  {generalVectors.map(vector => {
                    const vectorTypeLabel =
                      vectorTypeOptions.find(opt => opt.value === vector.vector_type)?.label ||
                      vector.vector_type;
                    return (
                      <MenuItem key={vector.id} value={vector.id}>
                        {vectorTypeLabel}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogOpen(false)}>Annulla</Button>
            <Button onClick={handleAddCustom} variant="outlined">
              Aggiungi custom
            </Button>
            <Button
              onClick={handleCopyVector}
              variant="contained"
              disabled={!selectedVectorId || copyMutation.isLoading}
            >
              Conferma
            </Button>
          </DialogActions>
        </Dialog>
      </>
    );
  })
);

DiagnosisEnergyVectorsStep.displayName = 'DiagnosisEnergyVectorsStep';
